import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { useSocket } from '../../context/SocketContext'
import { 
  FiHome, 
  FiUsers, 
  FiMessageSquare, 
  FiCheckSquare, 
  FiSettings,
  FiMenu,
  FiX,
  FiLogOut,
  FiBell,
  FiUser,
  FiTarget
} from 'react-icons/fi'
import { 
  RiRobotLine,
  RiDashboardLine,
  RiRadarLine
} from 'react-icons/ri'
import './Layout.css'

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const { user, logout } = useAuth()
  const { isConnected, agents } = useSocket()
  const navigate = useNavigate()

  const onlineAgents = agents.filter(a => a.status === 'online').length
  const totalAgents = agents.length || 17 // Default to 17 if no data

  const navItems = [
    { path: '/', label: 'Dashboard', icon: <RiDashboardLine /> },
    { path: '/agents', label: 'Agents', icon: <FiUsers />, badge: `${onlineAgents}/${totalAgents}` },
    { path: '/opportunities', label: 'Tenders', icon: <RiRadarLine /> },
    { path: '/clients', label: 'Clients', icon: <FiUsers /> },
    { path: '/leads', label: 'Local Leads', icon: <FiTarget /> },
    { path: '/chat', label: 'Chat with Kclaw', icon: <RiRobotLine /> },
    { path: '/tasks', label: 'Tasks', icon: <FiCheckSquare /> },
    { path: '/settings', label: 'Settings', icon: <FiSettings /> },
  ]

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo">
            <img src="/assets/logo.png" alt="TrenchMonkeyz" className="logo-icon-img" />
            <div className="logo-text">
              <span className="logo-subtitle">AI Agent Network</span>
            </div>
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            {sidebarOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => 
                `nav-item ${isActive ? 'active' : ''}`
              }
              end
            >
              <span className="nav-icon">{item.icon}</span>
              {sidebarOpen && (
                <>
                  <span className="nav-label">{item.label}</span>
                  {item.badge && (
                    <span className="nav-badge">{item.badge}</span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              <FiUser />
            </div>
            {sidebarOpen && (
              <div className="user-details">
                <div className="user-name">{user?.username || 'Mark'}</div>
                <div className="user-role">Admin</div>
              </div>
            )}
          </div>
          <button 
            className="logout-btn"
            onClick={handleLogout}
            title="Logout"
          >
            <FiLogOut />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        {/* Top header */}
        <header className="top-header">
          <div className="header-left">
            <button 
              className="mobile-menu-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle menu"
            >
              <FiMenu />
            </button>
            <div className="breadcrumb">
              <span className="connection-status">
                <span className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></span>
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>

          <div className="header-right">
            <button 
              className="notification-btn"
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              aria-label="Notifications"
            >
              <FiBell />
              <span className="notification-count">3</span>
            </button>

            <div className="user-menu">
              <div className="user-avatar-sm">
                <FiUser />
              </div>
              <div className="user-info-sm">
                <div className="user-name-sm">{user?.username || 'Mark'}</div>
                <div className="user-status">Online</div>
              </div>
            </div>
          </div>
        </header>

        {/* Notifications panel */}
        {notificationsOpen && (
          <div className="notifications-panel">
            <div className="notifications-header">
              <h3>Notifications</h3>
              <button 
                className="close-notifications"
                onClick={() => setNotificationsOpen(false)}
                aria-label="Close notifications"
              >
                <FiX />
              </button>
            </div>
            <div className="notifications-list">
              <button
                className="notification-item clickable"
                onClick={() => { setNotificationsOpen(false); navigate('/'); }}
              >
                <div className="notification-icon success">✓</div>
                <div className="notification-content">
                  <div className="notification-title">Dashboard updated</div>
                  <div className="notification-time">2 minutes ago</div>
                </div>
              </button>
              <button
                className="notification-item clickable"
                onClick={() => { setNotificationsOpen(false); navigate('/agents'); }}
              >
                <div className="notification-icon warning">!</div>
                <div className="notification-content">
                  <div className="notification-title">Agent Noah is busy</div>
                  <div className="notification-time">15 minutes ago</div>
                </div>
              </button>
              <button
                className="notification-item clickable"
                onClick={() => { setNotificationsOpen(false); navigate('/chat'); }}
              >
                <div className="notification-icon info">i</div>
                <div className="notification-content">
                  <div className="notification-title">New message from Kclaw</div>
                  <div className="notification-time">1 hour ago</div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Page content */}
        <div className="content-wrapper">
          <Outlet />
        </div>

        {/* Footer */}
        <footer className="main-footer">
          <div className="footer-content">
            <div className="footer-left">
              <span className="footer-text">
                TrenchMonkeyz AI Agent Dashboard v1.0.0
              </span>
            </div>
            <div className="footer-right">
              <span className="footer-text">
                {onlineAgents} agents online • Last updated: Just now
              </span>
            </div>
          </div>
        </footer>
      </main>
    </div>
  )
}

export default Layout